import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader } from "@/components/common/Loader";
import { EmptyState } from "@/components/common/EmptyState";
import { LeadCard } from "@/components/common/LeadCard";
import { CallButton } from "@/components/common/CallButton";
import { DealershipFormDialog } from "@/components/dealerships/DealershipFormDialog";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";
import {
  ArrowLeft, Store, MapPin, User, Pencil, Plus, Car, Phone,
} from "lucide-react";

function Stat({ label, value, tone }) {
  const tones = {
    default: "text-foreground", success: "text-success", warning: "text-warning",
    danger: "text-destructive", brand: "text-brand",
  };
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-center shadow-card">
      <div className={`tnum font-display text-xl font-extrabold ${tones[tone] || tones.default}`}>{value}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

export default function DealershipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/dealerships/${id}`)
      .then((res) => setData(res.data))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Could not load dealership");
        navigate("/dealerships", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader label="Loading dealership…" />;
  if (!data) return null;

  const { dealership: d, stats, leads } = data;

  return (
    <div className="animate-fade-in-up space-y-5">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="h-5 w-5" /></Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-xl font-bold text-foreground sm:text-2xl">{d.name || "Untitled Dealership"}</h1>
            {d.oem_brand && <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-secondary-foreground">{d.oem_brand}</span>}
          </div>
          {isLeader && d.owner_name && <p className="text-xs text-muted-foreground">Owned by {d.owner_name}</p>}
        </div>
        <Button variant="outline" size="icon" onClick={() => setEditOpen(true)}><Pencil className="h-4 w-4" /></Button>
      </div>

      {/* Contact card */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-card">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-accent-foreground"><Store className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1 space-y-1 text-sm">
            {d.contact_person && <div className="flex items-center gap-2 text-foreground"><User className="h-4 w-4 text-muted-foreground" /> {d.contact_person}</div>}
            {d.mobile && <div className="flex items-center gap-2 text-foreground"><Phone className="h-4 w-4 text-muted-foreground" /> {d.mobile}</div>}
            {(d.city || d.location) && <div className="flex items-start gap-2 text-muted-foreground"><MapPin className="mt-0.5 h-4 w-4" /> {[d.location, d.city].filter(Boolean).join(", ")}</div>}
          </div>
        </div>
        {d.remarks && <p className="mt-3 rounded-lg bg-secondary/60 p-2.5 text-xs text-muted-foreground">{d.remarks}</p>}
        <div className="mt-3 flex gap-2">
          {d.mobile && <CallButton number={d.mobile} full />}
          <Button variant="brand" className={d.mobile ? "flex-1" : "w-full"} onClick={() => navigate(`/leads/new?type=dealership&dealership_id=${d.id}`)}>
            <Plus className="h-4 w-4" /> New Lead
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-6">
        <Stat label="Total" value={stats.total_leads} tone="brand" />
        <Stat label="Active" value={stats.active_leads} />
        <Stat label="Deals" value={stats.deals_done} tone="success" />
        <Stat label="Completed" value={stats.completed_deals} tone="success" />
        <Stat label="Lost" value={stats.lost_leads} tone="danger" />
        <Stat label="Follow-ups" value={stats.pending_followups} tone="warning" />
      </div>

      {/* Lead history */}
      <section>
        <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Lead history</h2>
        {leads.length ? (
          <div className="space-y-2.5">{leads.map((l) => <LeadCard key={l.id} lead={{ ...l, dealership_name: d.name }} />)}</div>
        ) : (
          <EmptyState icon={Car} title="No leads yet" description="Add the first vehicle lead for this dealership."
            action={<Button variant="brand" onClick={() => navigate(`/leads/new?type=dealership&dealership_id=${d.id}`)}><Plus className="h-4 w-4" /> New Lead</Button>} />
        )}
      </section>

      <DealershipFormDialog open={editOpen} onOpenChange={setEditOpen} initial={d} onSaved={load} />
    </div>
  );
}
